import { isTimePermissionEnabled } from '../../config/applicationFeatures';
import React, { useMemo, useCallback, useState } from 'react';
import {
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { getOrganizationModuleCapabilities } from '../../config/organizationCapabilities';
import { getKioskModuleCapabilities } from '../../config/kioskCapabilities';
import { getShiftModuleCapabilities } from '../../config/shiftCapabilities';
import { getWorkPolicyModuleCapabilities } from '../../config/workPolicyCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  getCurrentOrganization,
  organizationStatusLabel,
} from '../../services/organizationService';
import type { OrganizationDetails } from '../../types/organization';
import { organizationErrorMessage } from '../../utils/organizationErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'OrganizationHome'>;

export function OrganizationHomeScreen({ navigation }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      section: {
        ...typography.sectionTitle,
        color: palette.textPrimary,
        marginBottom: spacing.sm,
        marginTop: spacing.md,
      },
      name: { ...typography.body, color: palette.textPrimary, fontWeight: '600' as const },
      meta: { ...typography.caption, color: palette.textSecondary },
    }),
    [palette],
  );

  const auth = useAuth();
  const capabilities = getOrganizationModuleCapabilities(auth.permissions);
  const kioskCapabilities = getKioskModuleCapabilities(auth.permissions);
  const shiftCapabilities = getShiftModuleCapabilities(auth.permissions);
  const workPolicyCapabilities = getWorkPolicyModuleCapabilities(
    auth.permissions,
  );

  const [org, setOrg] = useState<OrganizationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const data = await getCurrentOrganization();
      setOrg(data);
      setError(null);
    } catch (caught) {
      setOrg(null);
      setError(
        organizationErrorMessage(caught, 'Unable to load organization.'),
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.ORGANIZATION_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !org ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Organization"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading organization" />
        </AppScreen>
      ) : error && !org ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Organization"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={() => load()} />
        </AppScreen>
      ) : (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => load(true)}
            />
          }
        >
          <ScreenHeader
            title="Organization"
            onBack={navigation.goBack}
          />

          <View style={styles.body}>
            <Text style={themed.section}>Summary</Text>
            {org ? (
              <AppCard style={styles.card}>
                <Text style={themed.name}>{org.organizationName}</Text>
                <Text style={themed.meta}>
                  Code: {org.organizationCode || 'Not listed'}
                </Text>
                <Text style={themed.meta}>
                  {[org.city, org.countryCode].filter(Boolean).join(', ') ||
                    'Address not listed'}
                </Text>
                <Text style={themed.meta}>
                  Timezone: {org.timeZone || 'Not listed'}
                </Text>
                <Text style={themed.meta}>
                  Status: {organizationStatusLabel(org)}
                </Text>
              </AppCard>
            ) : null}

            <View style={styles.actions}>
              <AppButton
                title="View Organization"
                onPress={() => navigation.navigate(routes.organizationDetails)}
              />
              {capabilities.canUpdateOrganization ? (
                <AppButton
                  title="Edit Organization"
                  variant="secondary"
                  onPress={() => navigation.navigate(routes.organizationEdit)}
                />
              ) : null}
            </View>

            <Text style={themed.section}>Master data</Text>
            {capabilities.canViewLocations ? (
              <MenuRow
                color={palette.textPrimary}
                title="Locations"
                onPress={() => navigation.navigate(routes.locationList)}
              />
            ) : null}
            {capabilities.canViewDepartments ? (
              <MenuRow
                color={palette.textPrimary}
                title="Departments"
                onPress={() => navigation.navigate(routes.departmentList)}
              />
            ) : null}
            {capabilities.canViewDesignations ? (
              <MenuRow
                color={palette.textPrimary}
                title="Designations"
                onPress={() => navigation.navigate(routes.designationList)}
              />
            ) : null}
            {capabilities.canViewTeams ? (
              <MenuRow
                color={palette.textPrimary}
                title="Teams"
                onPress={() => navigation.navigate(routes.teamList)}
              />
            ) : null}
            {shiftCapabilities.canViewShifts ? (
              <MenuRow
                color={palette.textPrimary}
                title="Shifts"
                onPress={() => navigation.navigate(routes.shiftList)}
              />
            ) : null}
            {workPolicyCapabilities.canViewWorkPolicies ? (
              <MenuRow
                color={palette.textPrimary}
                title="Work Policies"
                onPress={() => navigation.navigate(routes.workPolicyList)}
              />
            ) : null}
            {auth.roles?.includes('HR_ADMIN') && auth.hasPermission(PERMISSIONS.SETTINGS_VIEW) ? (
              <MenuRow
                color={palette.textPrimary}
                title="WFH Configuration"
                onPress={() => navigation.navigate(routes.wfhConfigurationList)}
              />
            ) : null}
            {auth.roles?.includes('HR_ADMIN') && auth.hasPermission(PERMISSIONS.SETTINGS_VIEW) ? (
              <MenuRow
                color={palette.textPrimary}
                title="Late Arrival Configuration"
                onPress={() => navigation.navigate(routes.lateArrivalConfiguration)}
              />
            ) : null}
            {isTimePermissionEnabled() && auth.roles?.includes('HR_ADMIN') && auth.hasPermission(PERMISSIONS.SETTINGS_VIEW) ? (
              <MenuRow color={palette.textPrimary} title="Permission Configuration" onPress={() => navigation.navigate(routes.permissionConfigurationList)} />
            ) : null}
            {auth.roles?.includes('HR_ADMIN') && auth.hasPermission(PERMISSIONS.SETTINGS_VIEW) ? (
              <MenuRow
                color={palette.textPrimary}
                title="Leave Configuration"
                onPress={() => navigation.navigate(routes.leaveConfigurationList)}
              />
            ) : null}
            {auth.roles?.includes('HR_ADMIN') && auth.hasPermission(PERMISSIONS.SETTINGS_VIEW) ? (
              <MenuRow
                color={palette.textPrimary}
                title="Leave Balances"
                onPress={() => navigation.navigate(routes.leaveBalanceList)}
              />
            ) : null}
            {kioskCapabilities.canViewKiosks ? (
              <MenuRow
                color={palette.textPrimary}
                title="Kiosks"
                onPress={() => navigation.navigate(routes.kioskList)}
              />
            ) : null}

          </View>
        </AppScreen>
      )}
    </PermissionGuard>
  );
}

function MenuRow({
  title,
  onPress,
  color,
}: {
  title: string;
  onPress: () => void;
  color: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={title}
      onPress={onPress}
    >
      <AppCard style={styles.menuCard}>
        <Text style={[styles.menuItem, { color }]}>{title}</Text>
      </AppCard>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  center: { justifyContent: 'center' },
  body: { marginTop: spacing.lg },
  card: { gap: spacing.xs, marginBottom: spacing.sm },
  actions: { gap: spacing.sm, marginBottom: spacing.md },
  menuCard: { marginBottom: spacing.sm },
  menuItem: { ...typography.body, fontWeight: '600' },
});
